import { useTranslation } from "react-i18next"; /**
 * Push Notification Provider
 * Handles service worker registration and push subscription management
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import pushService from '@/shared/services/pushService';

const PushNotificationContext = createContext(null);

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotifications must be used within PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {const { t } = useTranslation("common");
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [swRegistration, setSwRegistration] = useState(null);

  // Initialize push notifications
  useEffect(() => {
    const init = async () => {
      // Check if supported
      const supported = pushService.isPushSupported();
      setIsSupported(supported);

      if (!supported) {
        setIsLoading(false);
        return;
      }

      // Check permission
      const currentPermission = pushService.checkNotificationPermission();
      setPermission(currentPermission);

      // Register service worker
      try {
        const registration = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        setSwRegistration(registration);
        console.log('[Push] Service worker registered:', registration.scope);

        // Check if already subscribed
        const subscribed = await pushService.isSubscribed();
        setIsSubscribed(subscribed);

      } catch (error) {
        console.error('[Push] Service worker registration failed:', error);
      }

      setIsLoading(false);
    };

    init();
  }, []);

  // Listen for service worker messages (notification clicks)
  useEffect(() => {
    if (!swRegistration) return;

    const handleMessage = (event) => {
      if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
        console.log('[Push] Notification clicked:', event.data);
        // Navigation will be handled by the component using this hook
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, [swRegistration]);

  // Subscribe to push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications not supported', {
        description: 'Your browser does not support push notifications'
      });
      return { success: false };
    }

    setIsLoading(true);

    try {
      const result = await pushService.subscribeToPush();

      if (result.success) {
        setIsSubscribed(true);
        setPermission('granted');
        toast.success('Notifications enabled', {
          description: 'You will receive push notifications for important alerts'
        });
      } else {
        toast.error('Failed to enable notifications', {
          description: result.error || 'Unknown error'
        });
      }

      return result;
    } catch (error) {
      toast.error('Failed to enable notifications');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Unsubscribe from push notifications
  const unsubscribe = useCallback(async () => {
    setIsLoading(true);

    try {
      const result = await pushService.unsubscribeFromPush();

      if (result.success) {
        setIsSubscribed(false);
        toast.info('Notifications disabled', {
          description: 'You will no longer receive push notifications'
        });
      }

      return result;
    } catch (error) {
      toast.error('Failed to disable notifications');
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Request permission only (without subscribing)
  const requestPermission = useCallback(async () => {
    const result = await pushService.requestNotificationPermission();
    setPermission(result.permission);
    return result;
  }, []);

  // Show a local notification (for testing)
  const showLocalNotification = useCallback((title, options = {}) => {
    pushService.showLocalNotification(title, options);
  }, []);

  const value = {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    requestPermission,
    showLocalNotification
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>);

};

export default PushNotificationProvider;